import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import UnsavedChangesModal from "../../components/UnsavedChangesModal/UnsavedChangesModal";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS as PROJECT_QUERY_KEYS } from "../../config/constants";
import {
  Typography,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Upload,
  Row,
  Col,
  Space,
  message,
  notification,
  Tag,
  Table,
  Divider,
  Popconfirm,
  Modal,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ProjectOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  UploadOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { uploadSOWDocument } from "../../services/sowApi";
import { MarkdownField } from "../../components/MarkdownText";
import { addProject } from "../../services/projectService";
import {
  API_BASE_URL,
  APPROVAL_USERS_ACCOUNT_API_URL,
  PROJECT_UPDATE_API_BASE_URL,
} from "../../config/constants";
import {
  canCreateProject,
  canEditProject,
  getCurrentUserRole,
  Role,
} from "../../config/rbac";
import {
  DATE_DISPLAY_FORMAT,
  toApiDateMmDdYyyy,
  toIsoDate,
} from "../../utils/dateFormat";
import { isMasked, toAmount } from "../../utils/maskedValue";
import { apiFetch } from "../../utils/apiClient";
import { useProjectList } from "../../hooks/projects/useProjectList";
import { requestNotificationsRefresh } from "../../services/notificationsApi";
import { useBusinessDomains } from "../../hooks/useBusinessDomain";
import DataState from "../../components/DataState/DataState";
import "../../styles/trackon.css"

const { Title, Text } = Typography;

const DEFAULT_MILESTONE_STATUS = "Not Submitted";

const MILESTONE_STATUS_OPTIONS = [
  { value: "Paid", label: "Paid" },
  { value: "Submitted", label: "Submitted" },
  { value: "Not Submitted", label: "Not Submitted" },
];

const LinearProgressWithLabelAndValue = ({ value }) => {
  const safeValue = Math.min(100, Math.max(0, Number(value) || 0));

  return (
    <Box sx={{ width: "100%" }}>
      <LinearProgress
        variant="determinate"
        value={safeValue}
        aria-label="SOW upload progress"
        sx={{
          height: 4,
          borderRadius: 2,
        }}
      />
    </Box>
  );
};

let milestoneUidCounter = 0;

const createMilestoneUid = () => {
  milestoneUidCounter += 1;
  return `ms-${Date.now()}-${milestoneUidCounter}`;
};

const CONTRACT_CATEGORY_OPTIONS = [
  { value: "T & M", label: "T & M" },
  {
    value: "Fixed Bid/Price-Milestone",
    label: "Fixed Bid/Price-Milestone",
  },
  {
    value: "Managed Capacity",
    label: "Managed Capacity",
  },
];

const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD" },
  { value: "INR", label: "INR" },
];

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const TOP_ROLES = [
  Role.PMO,
  Role.ADMIN,
  Role.ACCOUNT_MANAGER
];

const toYyyyMmDd = (value) => toIsoDate(value) ?? undefined;

const formatApiDate = (value) => toApiDateMmDdYyyy(value);

const MILESTONE_DATE_FORMAT = "MM-DD-YYYY";

const parseMilestoneDate = (value) => {
  const text = String(value ?? "").trim();
  const match = text.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{2}|\d{4})$/);

  if (!match) return null;

  const month = Number(match[1]);
  const day = Number(match[2]);
  const yearPart = match[3];
  const year = yearPart.length === 2 ? 2000 + Number(yearPart) : Number(yearPart);

  if (year < 1 || month < 1 || month > 12 || day < 1) {
    return null;
  }

  const date = dayjs(
    `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
  );

  if (!date.isValid()) return null;

  if (
    date.year() !== year ||
    date.month() + 1 !== month ||
    date.date() !== day
  ) {
    return null;
  }

  return date;
};

const normalizeMilestoneDate = (value) => {
  const parsed = parseMilestoneDate(value);
  return parsed ? parsed.format("MM-DD-YYYY") : String(value ?? "").trim();
};

// Normalize multiline milestone text from the backend into bullet points.
// A single line remains unchanged; once multiple lines exist, each line is
// displayed as a bullet while preserving already-entered bullet markers.
const normalizeMilestoneName = (value) => {
  const text = String(value ?? "").replace(/\r\n/g, "\n").trim();

  if (!text) return "";

  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  // A single-line milestone remains normal text. It becomes a bullet only
  // after the user creates a second line with Enter.
  if (lines.length <= 1) {
    return lines[0] || "";
  }

  return lines
    .map((line) => {
      const cleaned = line.replace(/^[-•]\s*/, "").trim();
      return cleaned ? `- ${cleaned}` : "";
    })
    .filter(Boolean)
    .join("\n");
};

const addMilestoneBulletLine = (value, start, end) => {
  const currentValue = String(value ?? "");
  const before = currentValue.slice(0, start);
  const selected = currentValue.slice(start, end);
  const after = currentValue.slice(end);
  const hasMultipleLines = currentValue.includes("\n");

  if (!hasMultipleLines) {
    const firstLine = before.trim();
    const firstLineWithBullet = firstLine
      ? `- ${firstLine}`
      : "- ";

    const nextLine = selected.trim() || after.trim();
    const nextValue = nextLine
      ? `${firstLineWithBullet}\n- ${nextLine}`
      : `${firstLineWithBullet}\n- `;

    return {
      value: nextValue,
      cursor: firstLineWithBullet.length + 3,
    };
  }

  const normalizedBefore = before.replace(/(^|\n)(?![-•]\s)/g, "$1- ");
  const insertion = "\n- ";
  const nextValue = normalizedBefore + insertion + selected + after;

  return {
    value: nextValue,
    cursor: normalizedBefore.length + insertion.length,
  };
};


const normalizeBusinessAreas = (value) => {
  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => String(item ?? "").trim())
      .filter((item) => item.length > 0);

    return normalized.length > 0 ? normalized : undefined;
  }

  if (typeof value === "string") {
    const normalized = value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    return normalized.length > 0 ? normalized : undefined;
  }

  return undefined;
};

const serializeBusinessAreas = (value) => {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined;
  }

  const normalized = value
    .map((item) => String(item ?? "").trim())
    .filter((item) => item.length > 0);

  return normalized.length > 0 ? normalized : undefined;
};

const readPcrIds = (project) => {
  if (!project) return [];

  const candidates = [
    project.pcrId,
    project.pcrID,
    project.PCRID,
    project.pcrid,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const normalized = candidate
        .map((value) => String(value ?? "").trim())
        .filter((value) => value.length > 0);

      if (normalized.length > 0) {
        return normalized;
      }
    }

    if (typeof candidate === "string") {
      const trimmed = candidate.trim();

      if (trimmed.length > 0) {
        return [trimmed];
      }
    }
  }

  return [];
};

const readStatus = (project) => {
  const rawStatus = project?.status;

  if (typeof rawStatus === "boolean") {
    return rawStatus ? "Active" : "Inactive";
  }

  if (typeof rawStatus === "string") {
    const normalized = rawStatus.trim().toLowerCase();

    if (!normalized) return "Active";

    return normalized === "inactive" || normalized === "false"
      ? "Inactive"
      : "Active";
  }

  return "Active";
};

const readAssignees = (
  project,
  listKey,
  fallbackIdKey,
  fallbackNameKey,
) => {
  if (!project) return [];

  const listValue = project[listKey];

  if (Array.isArray(listValue)) {
    const mapped = listValue
      .map((entry) => {
        const record = entry;
        const userId = String(record.userId ?? "").trim();
        const name = String(record.name ?? "").trim();

        if (!userId && !name) return null;

        return {
          userId,
          name,
        };
      })
      .filter((entry) => entry !== null);

    if (mapped.length > 0) {
      return mapped;
    }
  }

  const fallbackId = String(
    project[fallbackIdKey] ?? "",
  ).trim();

  const fallbackName = String(
    project[fallbackNameKey] ?? "",
  ).trim();

  if (!fallbackId && !fallbackName) return [];

  return [
    {
      userId: fallbackId,
      name: fallbackName,
    },
  ];
};

const normalizeProjectKey = (value) =>
  String(value || "").trim().toLowerCase();

const extractProjectCandidates = (payload) => {
  const candidates = [];

  const walk = (value) => {
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }

    if (value && typeof value === "object") {
      const obj = value;

      if (
        "projectId" in obj ||
        "projectID" in obj ||
        "project_id" in obj ||
        "id" in obj
      ) {
        candidates.push(obj);
      }

      Object.values(obj).forEach(walk);
    }
  };

  walk(payload);

  return candidates;
};

const buildEditFormValues = (
  project,
  accountOptions,
) => {
  const existingSow = project.sow;

  const accountIdCandidate =
    project.accountId ??
    project.customerId ??
    project.accountID ??
    project.customerID;

  const accountNameCandidate =
    project.accountName ||
    project.customerName ||
    "";

  const matchedAccountIdFromName =
    accountOptions.find(
      (opt) =>
        opt.label.trim().toLowerCase() ===
        accountNameCandidate.trim().toLowerCase(),
    )?.value;

  const parsedPcrIds = readPcrIds(project);

  const invoiceCurrencyType = Array.isArray(
    project.invoiceDetails,
  )
    ? project.invoiceDetails
      .map((invoice) =>
        String(invoice?.currencyType ?? "").trim(),
      )
      .find((currency) => currency.length > 0)
    : undefined;

  return {
    accountId: accountIdCandidate
      ? String(accountIdCandidate)
      : matchedAccountIdFromName,

    projectId: project.projectId
      ? String(project.projectId)
      : undefined,

    projectDescription:
      project.projectDescription ||
      project.projectName ||
      undefined,

    contractCategory: project.contractCategory,

    businessArea: normalizeBusinessAreas(
      project.businessArea,
    ),

    startDate: toYyyyMmDd(project.projectStartDate),

    endDate: toYyyyMmDd(project.projectEndDate),

    pcrId:
      parsedPcrIds.length > 0
        ? parsedPcrIds
        : [""],

    status: readStatus(project),

    plannedEbitdaPercentage:
      typeof project.plannedEbitdaPercentage === "number"
        ? project.plannedEbitdaPercentage
        : undefined,

    plannedGpmPercentage:
      typeof project.plannedGpmPercentage === "number"
        ? project.plannedGpmPercentage
        : undefined,

    sowId:
      typeof existingSow?.sowId === "string"
        ? existingSow.sowId
        : undefined,

    currencyType:
      typeof existingSow?.currencyType === "string" &&
        existingSow.currencyType.trim()
        ? existingSow.currencyType.trim().toUpperCase()
        : typeof project.currencyType === "string" &&
          project.currencyType.trim()
          ? project.currencyType.trim().toUpperCase()
          : invoiceCurrencyType?.trim().toUpperCase(),

    scope:
      typeof existingSow?.scopeOfWork === "string"
        ? existingSow.scopeOfWork
        : undefined,

    technologyRequirements:
      typeof existingSow?.technologyRequirements ===
        "string"
        ? existingSow.technologyRequirements
        : undefined,

    contractValue:
      toAmount(existingSow?.totalContractValue) ||
      undefined,
  };
};

const mapMilestonesToPayload = (
  milestoneItems,
  currencyType,
) =>
  milestoneItems.map((milestone) => {
    const milestoneStatus =
      milestone.status?.trim();

    return {
      milestoneId: milestone.milestoneId,
      milestoneName: milestone.milestoneName,
      startDate: normalizeMilestoneDate(milestone.startDate),
      endDate: normalizeMilestoneDate(milestone.endDate),
      amount: toAmount(milestone.amount),
      currencyType:
        currencyType?.trim() || undefined,
      status: milestoneStatus
        ? milestoneStatus
        : DEFAULT_MILESTONE_STATUS,
    };
  });

const toMilestone = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const row = value;

  const milestoneId = String(
    row.milestoneId ?? row.id ?? "",
  ).trim();

  const milestoneName = normalizeMilestoneName(
    row.milestoneName ?? row.name ?? "",
  );

  if (!milestoneId && !milestoneName) {
    return null;
  }

  return {
    uid: createMilestoneUid(),

    milestoneId:
      milestoneId || milestoneName,

    milestoneName:
      milestoneName || milestoneId,

    startDate: normalizeMilestoneDate(
      row.startDate ??
      row.expectedStartDate ??
      "",
    ),

    endDate: normalizeMilestoneDate(
      row.endDate ??
      row.expectedEndDate ??
      "",
    ),

    amount:
      toAmount(row.amount ?? row.value) || 0,

    status:
      String(row.status ?? "").trim() ||
      DEFAULT_MILESTONE_STATUS,
  };
};

const readProjectMilestones = (project) => {
  if (!project) return [];

  const directSow =
    project.sow &&
      typeof project.sow === "object"
      ? project.sow
      : null;

  const candidateLists = [
    directSow?.milestones,
    project.milestones,
    ...(Array.isArray(project.sows)
      ? project.sows.map(
        (sow) => sow?.milestones,
      )
      : []),
  ];

  for (const candidate of candidateLists) {
    if (
      !Array.isArray(candidate) ||
      candidate.length === 0
    ) {
      continue;
    }

    const mapped = candidate
      .map((item) => toMilestone(item))
      .filter((item) => item !== null);

    if (mapped.length > 0) {
      return mapped;
    }
  }

  return [];
};

const getDatePickerPopupContainer = () => document.body;

export function ProjectForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const returnPath = "/watchtower/projects";

  const { id } = useParams();

  const isEdit = Boolean(id);

  const [form] = Form.useForm();
  const isFormDirtyRef = useRef(false);
  const navigationBlocker = useUnsavedChangesGuard(isFormDirtyRef);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [uploadedFile, setUploadedFile] =
    useState("");

  const [sowData, setSowData] =
    useState(null);

  const [milestones, setMilestones] =
    useState([]);

  const [sowUploading, setSowUploading] =
    useState(false);

  const [sowUploadProgress, setSowUploadProgress] =
    useState(0);

  const [accountOptions, setAccountOptions] =
    useState([]);

  const [accountsLoading, setAccountsLoading] =
    useState(false);

  // Edit load state is keyed by `${id}:${attempt}`. A key that doesn't match
  // the current render means the load for this id/attempt hasn't reported yet,
  // so the status is 'loading' in the very first render (no flash of stale data).
  const [editLoadAttempt, setEditLoadAttempt] = useState(0);
  const editLoadKey = isEdit ? `${id}:${editLoadAttempt}` : "";

  const [editLoadState, setEditLoadState] =
    useState({ key: null, status: "loading", error: "" });

  const [editProjectState, setEditProjectState] =
    useState({ key: null, project: null });

  const editLoadStatus =
    editLoadState.key === editLoadKey ? editLoadState.status : "loading";

  const editLoadError =
    editLoadState.key === editLoadKey ? editLoadState.error : "";

  const editProject =
    editProjectState.key === editLoadKey ? editProjectState.project : null;

  const retryEditLoad = () => setEditLoadAttempt((attempt) => attempt + 1);

  const [editingCell, setEditingCell] = useState(null);
  const [milestoneModalOpen, setMilestoneModalOpen] = useState(false);
  const [milestoneForm] = Form.useForm();
  const milestoneTableRef = useRef(null);

  // Tracks which project has already been loaded into the edit form so
  // re-renders and list refetches don't overwrite the user's edits.
  const hydratedProjectKeyRef = useRef(null);

  const [notificationApi, notificationContextHolder] =
    notification.useNotification();

  const { data } = useProjectList();

  const currentRole = getCurrentUserRole();

  const canManageProjects =
    canCreateProject(currentRole) ||
    canEditProject(currentRole, {
      isAssignedProject: true,
    });

  const isTopRole = TOP_ROLES.includes(currentRole);
  const isPm =
    currentRole === Role.PROJECT_MANAGER;

  const {
    businessDomainOptions,
    loading,
  } = useBusinessDomains();

  const apiProject =
    isEdit && data?.rawProjects
      ? data.rawProjects.find(
        (p) =>
          p.id === id ||
          p.projectId === id,
      )
      : null;

  const activeProject =
    apiProject || editProject;

  // Create path has no load; edit path is ready once a project is resolved.
  const editPageStatus =
    !isEdit || activeProject
      ? "ready"
      : editLoadStatus === "error"
        ? "error"
        : "loading";

  // ID the project currently has on the server. The PATCH URL must use this;
  // the request body carries whatever the user typed into the Project ID field.
  const originalProjectId = String(
    activeProject?.projectId ?? id ?? "",
  ).trim();

  console.log(
    "Active Project:",
    activeProject,
  );

  const hasSection1ReadOnlyMode =
    isEdit && !isTopRole;
  const canEditMilestones = !isEdit || isTopRole;

  const watchedPcrIds = Form.useWatch(
    "pcrId",
    form,
  );

  const watchedCurrencyType =
    Form.useWatch(
      "currencyType",
      form,
    );

  // Keep milestone date constraints reactive when project dates change.
  const watchedProjectStartDate = Form.useWatch(
    "startDate",
    form,
  );
  const watchedProjectEndDate = Form.useWatch(
    "endDate",
    form,
  );

  // Edit page fields are always editable after
  // passing the page-level permission check.
  const inlineEditable = canEditMilestones;

  const existingOptionalFields = useMemo(() => {
    if (!isEdit || !activeProject) {
      return [];
    }

    const readText = (value) => {
      if (
        value === null ||
        value === undefined
      ) {
        return "";
      }

      return String(value).trim();
    };

    const cpAssignees = readAssignees(
      activeProject,
      "cp",
      "cpId",
      "cpName",
    );

    const dmAssignees = readAssignees(
      activeProject,
      "dm",
      "dmId",
      "dmName",
    );

    const pmAssignees = readAssignees(
      activeProject,
      "pm",
      "pmId",
      "pmName",
    );

    const candidates = [
      {
        key: "cpId",
        label: "Client Partner ID",
        value: cpAssignees
          .map((entry) => entry.userId)
          .filter(Boolean)
          .join(", "),
      },
      {
        key: "cpName",
        label: "Client Partner Name",
        value: cpAssignees
          .map((entry) => entry.name)
          .filter(Boolean)
          .join(", "),
      },
      {
        key: "dmId",
        label: "Delivery Manager ID",
        value: dmAssignees
          .map((entry) => entry.userId)
          .filter(Boolean)
          .join(", "),
      },
      {
        key: "dmName",
        label: "Delivery Manager Name",
        value: dmAssignees
          .map((entry) => entry.name)
          .filter(Boolean)
          .join(", "),
      },
      {
        key: "pmId",
        label: "Project Manager ID",
        value: pmAssignees
          .map((entry) => entry.userId)
          .filter(Boolean)
          .join(", "),
      },
      {
        key: "pmName",
        label: "Project Manager Name",
        value: pmAssignees
          .map((entry) => entry.name)
          .filter(Boolean)
          .join(", "),
      },
      {
        key: "accountCode",
        label: "Account Code",
        value: activeProject.accountCode,
      },
      {
        key: "customerCode",
        label: "Customer Code",
        value: activeProject.customerCode,
      },
    ];

    return candidates
      .map((item) => ({
        ...item,
        value: readText(item.value),
      }))
      .filter(
        (item) => item.value.length > 0,
      );
  }, [isEdit, activeProject]);

  useEffect(() => {
    if (
      !isEdit &&
      !canManageProjects
    ) {
      message.error(
        "You do not have permission to add projects.",
      );

      navigate(returnPath, {
        replace: true,
      });

      return;
    }

    if (
      isEdit &&
      !canManageProjects &&
      !isTopRole
    ) {
      message.error(
        "You do not have permission to edit projects.",
      );

      navigate(returnPath, {
        replace: true,
      });
    }
  }, [
    isEdit,
    canManageProjects,
    isTopRole,
    navigate,
  ]);

  useEffect(() => {
    const fetchProjectForEdit =
      async () => {
        if (
          !isEdit ||
          !id ||
          apiProject
        ) {
          return;
        }

        try {
          const rawToken =
            sessionStorage.getItem(
              "token",
            ) ||
            sessionStorage.getItem(
              "authToken",
            ) ||
            sessionStorage.getItem(
              "accessToken",
            );

          const tokenType =
            sessionStorage.getItem(
              "tokenType",
            ) || "Bearer";

          if (!rawToken) {
            throw new Error(
              "Authentication token not found. Please log in again.",
            );
          }

          const normalizedToken =
            rawToken
              .replace(
                /^"|"$/g,
                "",
              )
              .replace(
                /^Bearer\s+/i,
                "",
              )
              .trim();

          const target =
            normalizeProjectKey(id);

          const headers = {
            Authorization: `${tokenType} ${normalizedToken}`,
            "Content-Type":
              "application/json",
            Accept:
              "application/json",
          };

          let foundProject = null;
          let backendError = null;

          try {
            const response = await apiFetch(
              `${API_BASE_URL}projects/${encodeURIComponent(id)}`,
              {
                method: "GET",
                headers,
              },
              "Unable to load edit project.",
            );

            if (!response.ok) {
              const contentType =
                response.headers.get("content-type") || "";

              if (contentType.includes("application/json")) {
                const errorData = await response.json();
                console.error("Backend error:", errorData);

                backendError =
                  typeof errorData?.message === "string"
                    ? errorData.message.trim()
                    : typeof errorData?.error === "string"
                      ? errorData.error.trim()
                      : typeof errorData?.detail === "string"
                        ? errorData.detail.trim()
                        : "";
              } else {
                const errorText = await response.text();
                backendError = errorText.trim();
              }
            } else {
              const payload = await response.json();
              const candidates = extractProjectCandidates(payload);
              const target = normalizeProjectKey(id);

              foundProject =
                candidates.find(
                  (project) =>
                    [
                      project.projectId,
                      project.projectID,
                      project.project_id,
                      project.id,
                    ].some(
                      (value) =>
                        normalizeProjectKey(value) === target,
                    ),
                ) || null;
            }
          } catch (error) {
            console.error("Unable to load project:", error);
            backendError =
              error instanceof Error
                ? error.message
                : "Unable to load edit project.";
          }

          if (foundProject) {
            setEditProjectState({
              key: editLoadKey,
              project: foundProject,
            });
            setEditLoadState({
              key: editLoadKey,
              status: "ready",
              error: "",
            });
            return;
          }

          setEditLoadState({
            key: editLoadKey,
            status: "error",
            error: backendError
              || `Project details not found for ${id}.`,
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Unable to load edit project.";

          setEditLoadState({
            key: editLoadKey,
            status: "error",
            error: errorMessage,
          });
        }
      };

    void fetchProjectForEdit();
  }, [
    isEdit,
    id,
    apiProject,
    editLoadKey,
  ]);

  useEffect(() => {
    const loadAccounts =
      async () => {
        setAccountsLoading(true);

        try {
          const rawToken =
            sessionStorage.getItem(
              "token",
            ) ||
            sessionStorage.getItem(
              "authToken",
            ) ||
            sessionStorage.getItem(
              "accessToken",
            );

          const tokenType =
            sessionStorage.getItem(
              "tokenType",
            ) || "Bearer";

          if (!rawToken) {
            throw new Error(
              "Authentication token not found. Please log in again.",
            );
          }

          const normalizedToken =
            rawToken
              .replace(
                /^"|"$/g,
                "",
              )
              .replace(
                /^Bearer\s+/i,
                "",
              )
              .trim();

          const response =
            await apiFetch(
              APPROVAL_USERS_ACCOUNT_API_URL,
              {
                method: "GET",
                headers: {
                  Authorization: `${tokenType} ${normalizedToken}`,
                  "Content-Type":
                    "application/json",
                  Accept:
                    "application/json",
                },
              },
              "Failed to load customer accounts.",
            );

          const payload =
            await response.json();

          const source =
            Array.isArray(payload)
              ? payload
              : Array.isArray(
                payload?.data,
              )
                ? payload.data
                : Array.isArray(
                  payload?.content,
                )
                  ? payload.content
                  : [];

          const parsed = source
            .map((item) => {
              const accountId =
                item.accountId ??
                item.customerId ??
                item.id;

              const accountName =
                item.accountName ??
                item.customerName ??
                item.name;

              if (
                !accountId ||
                !accountName
              ) {
                return null;
              }

              return {
                value: String(
                  accountId,
                ),
                label: String(
                  accountName,
                ),
              };
            })
            .filter(
              (item) => item !== null,
            );

          setAccountOptions(
            parsed,
          );
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to load customer accounts.";

          message.error(
            errorMessage,
          );

          setAccountOptions(
            [],
          );
        } finally {
          setAccountsLoading(
            false,
          );
        }
      };

    void loadAccounts();
  }, [isEdit]);

  // Populate the form once per project so later re-renders or refetches
  // don't overwrite what the user has edited.
  useEffect(() => {
    if (!isEdit || !id || !activeProject) return;

    const projectKey = String(activeProject.projectId ?? id);

    if (hydratedProjectKeyRef.current === projectKey) return;

    hydratedProjectKeyRef.current = projectKey;

    form.setFieldsValue(
      buildEditFormValues(activeProject, accountOptions),
    );

    setMilestones(readProjectMilestones(activeProject));
  }, [isEdit, id, activeProject, form, accountOptions]);

  // Resolve / register the customer option without touching other fields.
  useEffect(() => {
    if (!isEdit || !activeProject) return;

    const currentAccountId = form.getFieldValue("accountId");

    if (!currentAccountId) {
      const { accountId } = buildEditFormValues(
        activeProject,
        accountOptions,
      );

      if (accountId) {
        form.setFieldValue("accountId", accountId);
      }

      return;
    }

    if (!accountOptions.some((opt) => opt.value === currentAccountId)) {
      setAccountOptions((prev) => [
        ...prev,
        {
          value: currentAccountId,
          label:
            activeProject.accountName ||
            activeProject.customerName ||
            currentAccountId,
        },
      ]);
    }
  }, [isEdit, activeProject, accountOptions, form]);

  const resolveProjectIdForSow = () =>
    String(form.getFieldValue("projectId") ?? "").trim() ||
    (isEdit ? originalProjectId : "");

  const handleSOWUpload =
    async (file) => {
      let progressTimer;

      try {
        const projectId =
          resolveProjectIdForSow();

        if (!projectId) {
          notificationApi.error({
            message: "Project ID required",
            description: "Enter Project ID before uploading the SOW.",
            placement: "topRight",
            duration: 3,
          });

          return false;
        }

        setSowUploading(true);
        setSowUploadProgress(8);

        progressTimer = setInterval(() => {
          setSowUploadProgress((prev) =>
            prev >= 92 ? prev : Math.min(92, prev + 3),
          );
        }, 250);

        const response =
          await uploadSOWDocument(
            file,
            projectId,
          );

        clearInterval(progressTimer);
        progressTimer = undefined;

        if (
          response.projectId &&
          response.projectId !==
          projectId
        ) {
          throw new Error(
            `Project ID mismatch: expected ${projectId}, received ${response.projectId}.`,
          );
        }

        // Show a completed progress state before populating the form.
        setSowUploadProgress(100);

        await new Promise((resolve) => {
          window.setTimeout(resolve, 350);
        });

        setUploadedFile(file.name);
        setSowData(response);

        setMilestones(
          Array.isArray(response.milestones)
            ? response.milestones.map((milestone) => ({
              ...milestone,
              uid: createMilestoneUid(),
              milestoneName: normalizeMilestoneName(milestone.milestoneName),
              status:
                String(milestone.status || "").trim() ||
                DEFAULT_MILESTONE_STATUS,
            }))
            : [],
        );

        form.setFieldsValue({
          sowId: response.sowId,
          currencyType: String(response.currencyType || "")
            .trim()
            .toUpperCase(),
          scope: response.scopeOfWork,
          technologyRequirements: response.technologyRequirements,
          contractValue:
            Number.parseFloat(response.totalContractValue || "0") || undefined,
        });

        window.setTimeout(() => {
          setSowUploading(false);
          setSowUploadProgress(0);
        }, 650);

        return false;
      } catch (error) {
        if (progressTimer) {
          clearInterval(progressTimer);
        }

        const errorMessage =
          error instanceof Error
            ? error.message
            : "SOW upload failed.";

        setSowUploading(false);
        setSowUploadProgress(0);

        message.error(
          errorMessage,
        );

        return false;
      }
    };

  const handleMilestoneFieldChange = (
    rowIndex,
    field,
    value,
  ) => {
    setMilestones((prev) =>
      prev.map((milestone, index) =>
        index === rowIndex
          ? {
            ...milestone,
            [field]: field === "milestoneName"
              ? String(value ?? "")
              : value,
          }
          : milestone,
      ),
    );
  };

  const getMilestoneFieldError = (
    milestone,
    index,
    field,
  ) => {
    const rowNumber = index + 1;
    const milestoneId = String(
      milestone?.milestoneId || "",
    ).trim();
    const milestoneName = String(
      milestone?.milestoneName || "",
    ).trim();
    const startDate = parseMilestoneDate(milestone?.startDate);
    const endDate = parseMilestoneDate(milestone?.endDate);
    const amount = Number(milestone?.amount);

    const projectStartValue = form.getFieldValue("startDate");
    const projectEndValue = form.getFieldValue("endDate");
    const projectStart = projectStartValue
      ? dayjs(toIsoDate(projectStartValue) || undefined)
      : null;
    const projectEnd = projectEndValue
      ? dayjs(toIsoDate(projectEndValue) || undefined)
      : null;

    if (field === "milestoneId" && !milestoneId) {
      return `Milestone Row ${rowNumber}: Milestone ID is required.`;
    }

    if (field === "milestoneName" && !milestoneName) {
      return `Milestone Row ${rowNumber}: Milestone name is required.`;
    }

    if (field === "startDate") {
      const rawStartDate = String(milestone?.startDate || "").trim();

      if (!rawStartDate) {
        return `Milestone Row ${rowNumber}: Start date is required.`;
      }

      if (!startDate || !startDate.isValid()) {
        return `Milestone Row ${rowNumber}: Start date must be a valid date in ${MILESTONE_DATE_FORMAT} format.`;
      }

      if (projectStart?.isValid() && startDate.isBefore(projectStart, "day")) {
        return `Milestone Row ${rowNumber}: Start date cannot be before the project start date.`;
      }

      if (projectEnd?.isValid() && startDate.isAfter(projectEnd, "day")) {
        return `Milestone Row ${rowNumber}: Start date cannot be after the project end date.`;
      }

      if (endDate?.isValid() && !endDate.isAfter(startDate, "day")) {
        return `Milestone Row ${rowNumber}: End date must be later than start date.`;
      }
    }

    if (field === "endDate") {
      const rawEndDate = String(milestone?.endDate || "").trim();

      if (!rawEndDate) {
        return `Milestone Row ${rowNumber}: End date is required.`;
      }

      if (!endDate || !endDate.isValid()) {
        return `Milestone Row ${rowNumber}: End date must be a valid date in ${MILESTONE_DATE_FORMAT} format.`;
      }

      if (projectStart?.isValid() && endDate.isBefore(projectStart, "day")) {
        return `Milestone Row ${rowNumber}: End date cannot be before the project start date.`;
      }

      if (projectEnd?.isValid() && endDate.isAfter(projectEnd, "day")) {
        return `Milestone Row ${rowNumber}: End date cannot be after the project end date.`;
      }

      if (startDate?.isValid() && !endDate.isAfter(startDate, "day")) {
        return `Milestone Row ${rowNumber}: End date must be later than start date.`;
      }
    }

    if (
      field === "amount" &&
      !isMasked(milestone?.amount) &&
      (!Number.isFinite(amount) || amount <= 0)
    ) {
      return `Milestone Row ${rowNumber}: Amount must be greater than zero.`;
    }

    return "";
  };

  const getMilestoneValidationError = (
    milestone,
    index,
  ) => {
    const fields = [
      "milestoneId",
      "milestoneName",
      "startDate",
      "endDate",
      "amount",
    ];

    return (
      fields
        .map((field) =>
          getMilestoneFieldError(
            milestone,
            index,
            field,
          ),
        )
        .find(Boolean) || ""
    );
  };

  const focusMilestoneRow = (uid) => {
    window.setTimeout(() => {
      const row = document.querySelector(
        `tr[data-row-key="${uid}"]`,
      );

      row?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 120);
  };

  const beginMilestoneEdit = (uid, field) => {
    if (!inlineEditable) return;
    setEditingCell({ uid, field });
  };

  const finishMilestoneEdit = () => {
    if (editingCell?.field === "milestoneName") {
      const index = milestones.findIndex(
        (milestone) => milestone.uid === editingCell.uid,
      );

      if (index >= 0) {
        setMilestones((prev) =>
          prev.map((milestone, milestoneIndex) =>
            milestoneIndex === index
              ? {
                ...milestone,
                milestoneName: normalizeMilestoneName(
                  milestone.milestoneName,
                ),
              }
              : milestone,
          ),
        );
      }
    } else if (
      editingCell?.field === "startDate" ||
      editingCell?.field === "endDate"
    ) {
      setMilestones((prev) =>
        prev.map((milestone) =>
          milestone.uid === editingCell.uid
            ? {
              ...milestone,
              [editingCell.field]: normalizeMilestoneDate(
                milestone[editingCell.field],
              ),
            }
            : milestone,
        ),
      );
    }

    setEditingCell(null);
  };

  useEffect(() => {
    if (editingCell?.field !== "status") return;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const clickedSelect = target.closest(".ant-select");
      const clickedDropdown = target.closest(".ant-select-dropdown");

      if (!clickedSelect && !clickedDropdown) {
        setEditingCell(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [editingCell]);

  const getProjectDate = (field) => {
    const value =
      field === "startDate"
        ? watchedProjectStartDate
        : field === "endDate"
          ? watchedProjectEndDate
          : form.getFieldValue(field);

    return value
      ? dayjs(toIsoDate(value) || undefined)
      : null;
  };

  const handleRemoveMilestone = (rowIndex) => {
    const removedUid = milestones[rowIndex]?.uid;

    setMilestones((prev) => prev.filter((_, index) => index !== rowIndex));

    if (editingCell?.uid === removedUid) {
      setEditingCell(null);
    }
  };

  const handleAddMilestone = () => {
    const invalidIndex = milestones.findIndex(
      (milestone, index) =>
        Boolean(
          getMilestoneValidationError(
            milestone,
            index,
          ),
        ),
    );

    if (invalidIndex !== -1) {
      const errorMessage =
        getMilestoneValidationError(
          milestones[invalidIndex],
          invalidIndex,
        );

      notificationApi.warning({
        message: "Complete the current milestone",
        description: errorMessage,
        placement: "topRight",
        duration: 4,
      });

      focusMilestoneRow(milestones[invalidIndex].uid);
      return;
    }

    milestoneForm.resetFields();
    milestoneForm.setFieldsValue({
      status: DEFAULT_MILESTONE_STATUS,
    });
    setMilestoneModalOpen(true);
  };

  const handleMilestoneModalCancel = () => {
    setMilestoneModalOpen(false);
    milestoneForm.resetFields();
  };

  const handleMilestoneModalAdd = async () => {
    try {
      const values = await milestoneForm.validateFields();
      const newMilestone = {
        uid: createMilestoneUid(),
        milestoneId: String(values.milestoneId || "").trim(),
        milestoneName: normalizeMilestoneName(
          String(values.milestoneName || "").trim(),
        ),
        startDate: normalizeMilestoneDate(values.startDate),
        endDate: normalizeMilestoneDate(values.endDate),
        amount: Number(values.amount || 0),
        status:
          values.status || DEFAULT_MILESTONE_STATUS,
      };

      setMilestones((prev) => [...prev, newMilestone]);
      setMilestoneModalOpen(false);
      milestoneForm.resetFields();
      setEditingCell(null);
      focusMilestoneRow(newMilestone.uid);

      notificationApi.success({
        message: "Milestone added",
        description: `${newMilestone.milestoneId} has been added to the milestone table.`,
        placement: "topRight",
        duration: 2.5,
      });
    } catch (_error) {
      // Ant Design displays the relevant field-level validation messages.
    }
  };

  const milestoneProjectStart = getProjectDate("startDate");
  const milestoneProjectEnd = getProjectDate("endDate");

  const handleSave =
    async () => {
      try {
        await form.validateFields();

        const invalidMilestoneIndex =
          milestones.findIndex(
            (milestone, index) =>
              Boolean(
                getMilestoneValidationError(
                  milestone,
                  index,
                ),
              ),
          );

        if (invalidMilestoneIndex !== -1) {
          const milestoneError =
            getMilestoneValidationError(
              milestones[invalidMilestoneIndex],
              invalidMilestoneIndex,
            );

          notificationApi.warning({
            message: "Milestone validation",
            description: milestoneError,
            placement: "topRight",
            duration: 4,
          });

          return;
        }

        setSaving(true);

        // `true` returns every value in the form store, including values
        // whose Form.Item isn't currently rendered.
        const formValues =
          form.getFieldsValue(true);

        const normalizedPcrIds =
          (
            formValues.pcrId ||
            []
          )
            .map((value) =>
              String(
                value || "",
              ).trim(),
            )
            .filter(
              (value) =>
                value.length > 0,
            );

        // Use the value from the Project ID field (editable by top roles);
        // fall back to the original ID only if the field is empty.
        const projectId =
          String(formValues.projectId ?? "").trim() ||
          (isEdit ? originalProjectId : "");

        if (!projectId) {
          throw new Error(
            "Project ID is missing.",
          );
        }

        if (isEdit && !originalProjectId) {
          throw new Error(
            "Unable to resolve the project being edited.",
          );
        }

        if (!formValues.accountId) {
          throw new Error(
            "Customer Name is required.",
          );
        }

        if (
          !formValues.projectDescription
        ) {
          throw new Error(
            "Project Name is required.",
          );
        }

        if (
          !formValues.contractCategory?.trim()
        ) {
          throw new Error(
            "Contract Category is required.",
          );
        }

        if (
          !formValues.startDate ||
          !formValues.endDate
        ) {
          throw new Error(
            "Project start and end dates are required.",
          );
        }

        const shouldSendSowPayload =
          Boolean(sowData) ||
          Boolean(
            String(formValues.sowId ?? "").trim(),
          ) ||
          Boolean(
            String(formValues.currencyType ?? "").trim(),
          ) ||
          Boolean(
            String(formValues.scope ?? "").trim(),
          ) ||
          Boolean(
            String(formValues.technologyRequirements ?? "").trim(),
          ) ||
          formValues.contractValue !==
          undefined ||
          milestones.length > 0;

        // The form is the single source of truth for SOW fields: an upload
        // copies its values into the form, so a field the user cleared must
        // stay cleared instead of falling back to the uploaded value.
        const resolvedCurrencyType =
          String(formValues.currencyType ?? "")
            .trim()
            .toUpperCase() || undefined;

        if (shouldSendSowPayload) {
          if (!resolvedCurrencyType) {
            throw new Error(
              "Currency type is required.",
            );
          }

          const contractValue = Number(
            formValues.contractValue,
          );

          if (
            !isMasked(formValues.contractValue) &&
            (!Number.isFinite(contractValue) ||
              contractValue <= 0)
          ) {
            throw new Error(
              "Total contract value must be a valid positive number.",
            );
          }

          if (!String(formValues.scope ?? "").trim()) {
            throw new Error(
              "Scope of work is required when SOW is provided.",
            );
          }
        }

        const sowPayload =
          shouldSendSowPayload
            ? {
              sowId:
                String(formValues.sowId ?? "").trim(),

              projectId,

              currencyType:
                resolvedCurrencyType,

              scopeOfWork:
                String(formValues.scope ?? "").trim(),

              technologyRequirements:
                String(formValues.technologyRequirements ?? "").trim(),

              totalContractValue:
                formValues.contractValue !== undefined &&
                  formValues.contractValue !== null
                  ? String(formValues.contractValue)
                  : "",

              sowExpectedStartDate:
                formatApiDate(
                  sowData?.sowExpectedStartDate ||
                  formValues.startDate,
                ),

              sowExpectedEndDate:
                formatApiDate(
                  sowData?.sowExpectedEndDate ||
                  formValues.endDate,
                ),

              milestones:
                mapMilestonesToPayload(
                  milestones,
                  resolvedCurrencyType,
                ),
            }
            : undefined;

        const selectedAccountName =
          accountOptions.find(
            (opt) =>
              opt.value ===
              formValues.accountId,
          )?.label || "";

        const addProjectPayload =
        {
          projectId,

          accountId:
            formValues.accountId,

          accountName:
            selectedAccountName,

          projectDescription:
            formValues.projectDescription,

          contractCategory:
            formValues.contractCategory ||
            "",

          businessArea:
            serializeBusinessAreas(
              formValues.businessArea,
            ) || [],

          projectStartDate:
            formatApiDate(
              formValues.startDate,
            ),

          projectEndDate:
            formatApiDate(
              formValues.endDate,
            ),

          status: formValues.status
            ? formValues.status ===
            "Active"
            : true,

          plannedEbitdaPercentage:
            formValues.plannedEbitdaPercentage,

          plannedGpmPercentage:
            formValues.plannedGpmPercentage,

          pcrId:
            normalizedPcrIds,

          ...(sowPayload
            ? {
              sow: sowPayload,
            }
            : {}),
        };

        const requestBody =
          isEdit
            ? addProjectPayload
            : {
              accountId:
                formValues.accountId,

              customerName:
                selectedAccountName,

              projectId,

              projectDescription:
                formValues.projectDescription,

              contractCategory:
                formValues.contractCategory,

              businessArea:
                serializeBusinessAreas(
                  formValues.businessArea,
                ),

              projectStartDate:
                formatApiDate(
                  formValues.startDate,
                ),

              projectEndDate:
                formatApiDate(
                  formValues.endDate,
                ),

              plannedEbitdaPercentage:
                formValues.plannedEbitdaPercentage,

              plannedGpmPercentage:
                formValues.plannedGpmPercentage,

              pcrId:
                normalizedPcrIds,
            };

        if (isEdit) {
          const token =
            sessionStorage.getItem(
              "token",
            ) ||
            sessionStorage.getItem(
              "authToken",
            ) ||
            sessionStorage.getItem(
              "accessToken",
            );

          const tokenType =
            sessionStorage.getItem(
              "tokenType",
            ) || "Bearer";

          if (!token) {
            message.error(
              "Authentication token not found. Please log in again.",
            );

            setSaving(false);

            return;
          }

          const normalizedToken =
            token
              .replace(
                /^"|"$/g,
                "",
              )
              .replace(
                /^Bearer\s+/i,
                "",
              )
              .trim();

          // URL targets the project as it exists on the server; the body
          // carries the (possibly changed) projectId.
          const response =
            await apiFetch(
              `${PROJECT_UPDATE_API_BASE_URL}/${encodeURIComponent(originalProjectId)}`,
              {
                method: "PATCH",
                headers: {
                  Authorization: `${tokenType} ${normalizedToken}`,
                  "Content-Type":
                    "application/json",
                },
                body: JSON.stringify(
                  requestBody,
                ),
              },
              "Failed to update project",
            );

          if (!response.ok) {
            const errorData =
              await response
                .json()
                .catch(
                  () => ({}),
                );

            throw new Error(
              errorData.message ||
              `Failed to update project: ${response.statusText}`,
            );
          }

          requestNotificationsRefresh();
        } else {
          await addProject(
            addProjectPayload,
          );
        }

        setSaving(false);

        // Drop cached project list/details so the next screen always fetches fresh data
        // (important when the projectId itself was changed).
        queryClient.removeQueries({ queryKey: [PROJECT_QUERY_KEYS.projects] });
        queryClient.removeQueries({ queryKey: ["project"] });
        queryClient.removeQueries({ queryKey: ["project-review"] });
        setSaved(true);
        isFormDirtyRef.current = false;

        form.resetFields();

        setMilestones([]);

        setSowData(null);

        setUploadedFile("");


        message.success(
          isEdit
            ? "Project updated successfully!"
            : "Project created successfully!",
        );

        setTimeout(() => {
          navigate(returnPath);
        }, 1000);
      } catch (error) {
        setSaving(false);

        const errorMessage =
          error instanceof Error
            ? error.message
            : "Please enter the correct data and try again.";

        if (error?.errorFields?.length) {
          const validationMessages =
            error.errorFields
              .flatMap((field) =>
                Array.isArray(field.errors)
                  ? field.errors
                  : [],
              )
              .filter(Boolean);

          notificationApi.error({
            message: "Please review the form",
            description:
              validationMessages.length > 0
                ? validationMessages.slice(0, 4).join(" ")
                : "Some required fields are missing or invalid.",
            placement: "topRight",
            duration: 5,
          });

          return;
        }

        notificationApi.error({
          message: "Unable to save project",
          description: errorMessage,
          placement: "topRight",
          duration: 5,
        });
      }
    };

  if (saved) {
    return (
      <div>
        {notificationContextHolder}
        <UnsavedChangesModal blocker={navigationBlocker} />
        <div
          style={{
            marginBottom: 24,
          }}
        >
          <Button
            icon={
              <ArrowLeftOutlined />
            }
            onClick={() =>
              navigate(
                returnPath,
              )
            }
          />
        </div>

        <Card
          style={{
            borderRadius: 16,
            border:
              "1px solid #f0f0f0",
            textAlign: "center",
            padding:
              "48px 24px",
          }}
        >
          <CheckCircleOutlined
            style={{
              fontSize: 72,
              color: "#52c41a",
              marginBottom: 16,
            }}
          />

          <Title level={4}>
            {isEdit
              ? "Project Updated!"
              : "Project Created!"}
          </Title>

          <Text
            style={{
              color: "#8c8c8c",
              display: "block",
              marginBottom: 28,
            }}
          >
            {isEdit
              ? `Changes to ${activeProject?.projectName ||
              activeProject?.projectDescription
              } have been saved.`
              : "Your new project has been added to the system."}
          </Text>

          <Space size={12}>
            <Button
              onClick={() => {
                setSaved(false);
                form.resetFields();
              }}
            >
              {isEdit
                ? "Edit Again"
                : "Add Another"}
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  const currencyType = String(
    watchedCurrencyType ||
    sowData?.currencyType ||
    "",
  )
    .trim()
    .toUpperCase();

  const currencySymbol =
    currencyType === "INR"
      ? "₹"
      : currencyType === "USD"
        ? "$"
        : "";

  return (
    <div>
      {notificationContextHolder}
      <UnsavedChangesModal blocker={navigationBlocker} />
      <DataState
        status={editPageStatus}
        errorTitle="Couldn't load project"
        error={editLoadError}
        onRetry={retryEditLoad}
        skeletonRows={6}
      >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <Button
          icon={
            <ArrowLeftOutlined />
          }
          onClick={() =>
            navigate(returnPath)
          }
        />

        <div>
          <Title
            level={3}
            style={{
              margin: 0,
            }}
          >
            {isEdit
              ? `Edit Project — ${activeProject?.projectId ??
              id
              }`
              : "Add New Project"}
          </Title>

          <Text
            style={{
              color: "#8c8c8c",
            }}
          >
            {isEdit
              ? "Update project status details"
              : "Fill in project details to create a new project"}
          </Text>
        </div>

        {isEdit &&
          activeProject && (
            <Tag
              color="blue"
              style={{
                marginLeft: "auto",
              }}
            >
              Editing
            </Tag>
          )}
      </div>

      <Form
        form={form}
        layout="vertical"
        size="large"
        initialValues={{
          pcrId: [""],
        }}
        onValuesChange={() => {
          isFormDirtyRef.current = true;
        }}
      >
        {/* SECTION 1 */}
        <Card
          style={{
            borderRadius: 16,
            border:
              "1px solid #f0f0f0",
            marginBottom: 20,
          }}
          title={
            <Space>
              <div
                style={{
                  width: 32,
                  height: 32,
                  background:
                    "#e6f4ff",
                  borderRadius: 8,
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                }}
              >
                <ProjectOutlined
                  style={{
                    color:
                      "#1677ff",
                    fontSize: 16,
                  }}
                />
              </div>

              <div>
                <Text
                  strong
                  style={{
                    fontSize: 15,
                  }}
                >
                  Section 1: Project
                  Details
                </Text>

                <Text
                  style={{
                    display: "block",
                    fontSize: 12,
                    color:
                      "#8c8c8c",
                    fontWeight: 400,
                  }}
                />
              </div>
            </Space>
          }
        >
          <Row gutter={[20, 0]}>
            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Customer Name"
                name="accountId"
                required
                rules={
                  isEdit
                    ? []
                    : [
                      {
                        required: true,
                        message:
                          "Customer Name is required",
                      },
                    ]
                }
              >
                <Select
                  placeholder="Select customer"
                  loading={
                    accountsLoading
                  }
                  showSearch
                  optionFilterProp="label"
                  options={
                    accountOptions
                  }
                  disabled={
                    hasSection1ReadOnlyMode
                  }
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Project ID"
                name="projectId"
                rules={[
                  {
                    required: true,
                    message:
                      "Project ID is required",
                  },
                ]}
              >
                <Input
                  placeholder="Enter project ID"
                  style={{
                    fontFamily:
                      "monospace",
                  }}
                  disabled={hasSection1ReadOnlyMode}
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Project Name"
                name="projectDescription"
                rules={[
                  {
                    required: true,
                    message:
                      "Project Name is required",
                  },
                ]}
              >
                <Input
                  placeholder="Enter project name"
                  disabled={
                    hasSection1ReadOnlyMode
                  }
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Contract Category"
                name="contractCategory"
                rules={[
                  {
                    required: true,
                    message:
                      "Contract Category is required",
                  },
                ]}
              >
                <Select
                  placeholder="Select contract category"
                  options={
                    CONTRACT_CATEGORY_OPTIONS
                  }
                  disabled={
                    hasSection1ReadOnlyMode
                  }
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Planned EBITDA (%)"
                name="plannedEbitdaPercentage"
              >
                <InputNumber
                  min={0}
                  max={100}
                  step={0.01}
                  placeholder="Enter planned EBITDA %"
                  style={{
                    width: "100%",
                  }}
                  disabled={
                    hasSection1ReadOnlyMode
                  }
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Planned GPM (%)"
                name="plannedGpmPercentage"
              >
                <InputNumber
                  min={0}
                  max={100}
                  step={0.01}
                  placeholder="Enter planned GPM %"
                  style={{
                    width: "100%",
                  }}
                  disabled={
                    hasSection1ReadOnlyMode
                  }
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Project Start Date"
                name="startDate"
                getValueFromEvent={(
                  date,
                ) =>
                  date
                    ? date.format(
                      DATE_DISPLAY_FORMAT,
                    )
                    : undefined
                }
                getValueProps={(
                  value,
                ) => ({
                  value: value
                    ? dayjs(
                      toIsoDate(
                        value,
                      ) ||
                      undefined,
                    )
                    : null,
                })}
                rules={[
                  {
                    required: true,
                    message:
                      "Project Start Date is required",
                  },
                ]}
              >
                <DatePicker
                  style={{
                    width: "100%",
                  }}
                  format={
                    DATE_DISPLAY_FORMAT
                  }
                  getPopupContainer={getDatePickerPopupContainer}
                  disabled={
                    hasSection1ReadOnlyMode
                  }
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Project End Date"
                name="endDate"
                dependencies={[
                  "startDate",
                ]}
                getValueFromEvent={(
                  date,
                ) =>
                  date
                    ? date.format(
                      DATE_DISPLAY_FORMAT,
                    )
                    : undefined
                }
                getValueProps={(
                  value,
                ) => ({
                  value: value
                    ? dayjs(
                      toIsoDate(
                        value,
                      ) ||
                      undefined,
                    )
                    : null,
                })}
                rules={[
                  {
                    required: true,
                    message:
                      "Project End Date is required",
                  },

                  ({
                    getFieldValue,
                  }) => ({
                    validator(
                      _,
                      value,
                    ) {
                      const startDate =
                        getFieldValue(
                          "startDate",
                        );

                      const end =
                        dayjs(
                          toIsoDate(
                            value,
                          ),
                        );

                      const start =
                        dayjs(
                          toIsoDate(
                            startDate,
                          ),
                        );

                      if (
                        !end.isValid() ||
                        !start.isValid()
                      ) {
                        return Promise.resolve();
                      }

                      if (
                        !end.isAfter(
                          start,
                        )
                      ) {
                        return Promise.reject(
                          new Error(
                            "Project End Date must be later than Project Start Date.",
                          ),
                        );
                      }

                      if (
                        end.isBefore(
                          start.add(
                            1,
                            "month",
                          ),
                        )
                      ) {
                        return Promise.reject(
                          new Error(
                            "Project End Date must be at least 1 month after Project Start Date.",
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  style={{
                    width: "100%",
                  }}
                  format={
                    DATE_DISPLAY_FORMAT
                  }
                  getPopupContainer={getDatePickerPopupContainer}
                  disabled={
                    hasSection1ReadOnlyMode
                  }
                />
              </Form.Item>
            </Col>

            {/* PCR ID */}
            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label={
                  <Space
                    size={10}
                    align="center"
                  >
                    <Text strong>
                      PCR ID
                    </Text>

                    {!hasSection1ReadOnlyMode && (
                      <Button
                        type="default"
                        size="small"
                        icon={
                          <PlusOutlined />
                        }
                        onClick={() => {
                          const current =
                            form.getFieldValue(
                              "pcrId",
                            );

                          if (
                            !Array.isArray(
                              current,
                            ) ||
                            current.length ===
                            0
                          ) {
                            form.setFieldValue(
                              "pcrId",
                              [""],
                            );

                            notificationApi.info({
                              message: "PCR ID field added",
                              description: "Enter the PCR ID before adding another field.",
                              placement: "topRight",
                              duration: 2.5,
                            });

                            return;
                          }

                          const lastValue =
                            String(
                              current[
                              current.length -
                              1
                              ] || "",
                            ).trim();

                          if (
                            lastValue.length ===
                            0
                          ) {
                            notificationApi.warning({
                              message: "PCR ID required",
                              description: "Fill the last PCR ID before adding a new field.",
                              placement: "topRight",
                              duration: 3,
                            });

                            return;
                          }

                          form.setFieldValue(
                            "pcrId",
                            [
                              ...current,
                              "",
                            ],
                          );
                        }}
                        aria-label="Add PCR ID"
                        style={{
                          borderRadius:
                            999,
                          borderColor:
                            "#91caff",
                          color:
                            "#0958d9",
                          background:
                            "linear-gradient(180deg, #f0f9ff 0%, #e6f4ff 100%)",
                          fontWeight: 600,
                        }}
                      >
                        Add Field
                      </Button>
                    )}
                  </Space>
                }
              >
                <Form.List name="pcrId">
                  {(
                    fields,
                    {
                      remove,
                    },
                  ) => (
                    <div
                      style={{
                        width: "100%",
                      }}
                    >
                      {fields.map(
                        (
                          field,
                        ) => (
                          <Row
                            key={
                              field.key
                            }
                            gutter={8}
                            align="top"
                            style={{
                              marginBottom:
                                8,
                            }}
                          >
                            <Col flex="1">
                              <Form.Item
                                {...field}
                                style={{
                                  marginBottom:
                                    0,
                                }}
                                rules={[
                                  {
                                    validator:
                                      async (
                                        _,
                                        value,
                                      ) => {
                                        if (
                                          !value ||
                                          String(
                                            value,
                                          ).trim()
                                            .length >
                                          0
                                        ) {
                                          return;
                                        }

                                        throw new Error(
                                          "PCRID cannot be empty.",
                                        );
                                      },
                                  },
                                ]}
                              >
                                <Input
                                  placeholder="Enter PCR ID"
                                  disabled={
                                    hasSection1ReadOnlyMode
                                  }
                                />
                              </Form.Item>
                            </Col>

                            {!hasSection1ReadOnlyMode &&
                              fields.length >
                              1 && (
                                <Col flex="40px">
                                  <Button
                                    aria-label="Remove PCR ID"
                                    icon={
                                      <MinusCircleOutlined />
                                    }
                                    onClick={() =>
                                      remove(
                                        field.name,
                                      )
                                    }
                                    style={{
                                      width: 40,
                                    }}
                                  />
                                </Col>
                              )}
                          </Row>
                        ),
                      )}
                    </div>
                  )}
                </Form.List>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <span style={{ display: "inline-flex", alignItems: "center", minHeight: 24 }}>
                    Business Area
                  </span>
                }
                name="businessArea"
              >
                <Select
                  className="business-area-select"
                  mode="multiple"
                  allowClear
                  placeholder="Select business area(s)"
                  maxTagCount={999}
                  loading={loading}
                  options={businessDomainOptions}
                  disabled={hasSection1ReadOnlyMode}
                  popupMatchSelectWidth
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            {isEdit && (
              <Col
                xs={24}
                md={12}
              >
                <Form.Item
                  label="Status"
                  name="status"
                  rules={[
                    {
                      required: true,
                      message:
                        "Status is required",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select status"
                    options={
                      STATUS_OPTIONS
                    }
                    disabled={
                      hasSection1ReadOnlyMode
                    }
                  />
                </Form.Item>
              </Col>
            )}

            {isEdit &&
              existingOptionalFields.map(
                (field) => (
                  <Col
                    xs={24}
                    md={12}
                    key={field.key}
                  >
                    <Form.Item
                      label={
                        field.label
                      }
                    >
                      <Input
                        value={
                          field.value
                        }
                        disabled
                      />
                    </Form.Item>
                  </Col>
                ),
              )}
          </Row>
        </Card>

        {/* SECTION 2 */}
        <Card
          style={{
            borderRadius: 16,
            border:
              "1px solid #f0f0f0",
            marginBottom: 24,
          }}
          title={
            <Space>
              <div
                style={{
                  width: 32,
                  height: 32,
                  background:
                    "#f6ffed",
                  borderRadius: 8,
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                }}
              >
                <FileTextOutlined
                  style={{
                    color:
                      "#52c41a",
                    fontSize: 16,
                  }}
                />
              </div>

              <Text
                strong
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Section 2: SOW Details
              </Text>
            </Space>
          }
          extra={
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                width: 200,
                flexShrink: 0,
              }}
            >
              <Upload
                accept=".pdf,.doc,.docx"
                maxCount={1}
                showUploadList={false}
                beforeUpload={(file) => {
                  const allowed = [
                    "application/pdf",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  ];

                  if (!allowed.includes(file.type)) {
                    notificationApi.error({
                      message: "Unsupported file",
                      description: "Only PDF, DOC, and DOCX files are allowed.",
                      placement: "topRight",
                      duration: 3,
                    });
                    return Upload.LIST_IGNORE;
                  }

                  void handleSOWUpload(file);
                  return false;
                }}
                disabled={sowUploading}
              >
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  loading={sowUploading}
                  disabled={sowUploading}
                  style={{
                    borderRadius: 4,
                    height: 40,
                    width: 200,
                    paddingInline: 12,
                    fontWeight: 450,
                    whiteSpace: "nowrap",
                  }}
                >
                  Upload SOW File
                </Button>
              </Upload>

              {(sowUploading || sowUploadProgress > 0) && (
                <div style={{ width: 200, marginTop: 6 }}>
                  <LinearProgressWithLabelAndValue value={sowUploadProgress} />
                </div>
              )}
            </div>
          }
        >


          <Row
            gutter={[
              20,
              0,
            ]}
          >
            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="SOW ID"
                name="sowId"
              >
                <Input
                  placeholder="SOW ID"
                  disabled={
                    !inlineEditable
                  }
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Project Currency Type"
                name="currencyType"
                rules={[
                  {
                    validator: (_, value) => {
                      const hasSow =
                        Boolean(sowData) ||
                        milestones.length > 0 ||
                        Boolean(
                          form.getFieldValue("sowId")?.trim(),
                        ) ||
                        form.getFieldValue("contractValue") !==
                        undefined;

                      if (!hasSow || value) {
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error(
                          "Currency type is required.",
                        ),
                      );
                    },
                  },
                ]}
              >
                <Select
                  placeholder="Select currency type"
                  options={
                    CURRENCY_OPTIONS
                  }
                  disabled={
                    !inlineEditable
                  }
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              md={12}
            >
              <Form.Item
                label="Total Contract Value"
                name="contractValue"
                rules={[
                  {
                    validator: (_, value) => {
                      const hasSow =
                        Boolean(sowData) ||
                        milestones.length > 0 ||
                        Boolean(
                          form.getFieldValue("sowId")?.trim(),
                        ) ||
                        Boolean(
                          form.getFieldValue("scope")?.trim(),
                        );

                      if (!hasSow && value === undefined) {
                        return Promise.resolve();
                      }

                      if (
                        value === undefined ||
                        value === null ||
                        value === ""
                      ) {
                        return Promise.reject(
                          new Error(
                            "Total contract value is required.",
                          ),
                        );
                      }

                      const numericValue = Number(value);

                      if (
                        !isMasked(value) &&
                        (!Number.isFinite(numericValue) ||
                          numericValue <= 0)
                      ) {
                        return Promise.reject(
                          new Error(
                            "Total contract value must be a valid positive number.",
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                {isMasked(form.getFieldValue("contractValue")) ? (
                  <Input
                    disabled
                    prefix={currencySymbol}
                    value={form.getFieldValue("contractValue")}
                  />
                ) : (
                <InputNumber
                  min={0}
                  style={{
                    width: "100%",
                  }}
                  disabled={
                    !inlineEditable
                  }
                  placeholder="Total contract value"
                  prefix={
                    currencySymbol
                  }
                />
                )}
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                label="Scope of Work"
                name="scope"
                rules={[
                  {
                    validator: (_, value) => {
                      const hasSow =
                        Boolean(sowData) ||
                        milestones.length > 0 ||
                        Boolean(
                          form.getFieldValue("sowId")?.trim(),
                        ) ||
                        form.getFieldValue("contractValue") !==
                        undefined;

                      if (!hasSow || String(value || "").trim()) {
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error(
                          "Scope of work is required when SOW is provided.",
                        ),
                      );
                    },
                  },
                ]}
              >
                <MarkdownField
                  placeholder="Scope of work"
                  disabled={!inlineEditable}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                label="Technology Requirements"
                name="technologyRequirements"
              >
                <MarkdownField
                  placeholder="Technology requirements"
                  disabled={!inlineEditable}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* MILESTONES */}
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
                gap: 16,
              }}
            >
              <div>
                <Typography.Title
                  level={5}
                  style={{ margin: 0 }}
                >
                  Milestone Details
                </Typography.Title>
              </div>

              {inlineEditable && (
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  onClick={handleAddMilestone}
                  style={{
                    height: 40,
                    width: "auto",
                    borderRadius: "4px",
                    paddingInline: 16,
                    fontWeight: 450,
                  }}
                >
                  Add Milestone
                </Button>
              )}
            </div>
             {milestones.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  marginTop: 4,
                  marginBottom: 8,
                  color: "#98a2b3",
                  fontSize: 11,
                  fontWeight: 400,
                  lineHeight: 1.4,
                }}
              >
                <InfoCircleOutlined style={{ fontSize: 11 }} />
                <span>Click any field to edit its value.</span>
              </div>
            )}

            <Table
              className="modern-milestone-table"
              bordered={false}
              size="small"
              pagination={false}
              scroll={{ y: 320, x: 900 }}
              rowKey={(record) => record.uid}
              dataSource={milestones}
              rowClassName={(record) =>
                editingCell?.uid === record.uid
                  ? "milestone-editing-row"
                  : ""
              }
              locale={{
                emptyText:
                  "No milestones yet. Add one or upload an SOW to populate them.",
              }}
              columns={[
                {
                  title: "No.",
                  key: "rowNumber",
                  width: 55,
                  align: "center",
                  render: (_, record, index) => index + 1,
                },
                {
                  title: "Milestone ID",
                  dataIndex: "milestoneId",
                  key: "milestoneId",
                  width: "12%",
                  align: "left",
                  render: (value, record) => {
                    const field = "milestoneId";
                    const isEditing =
                      editingCell?.uid === record.uid &&
                      editingCell?.field === field;

                    if (isEditing) {
                      return (
                        <Input
                          size="small"
                          value={value}
                          placeholder="Milestone ID"
                          autoFocus
                          onChange={(e) =>
                            handleMilestoneFieldChange(
                              milestones.findIndex(
                                (item) => item.uid === record.uid,
                              ),
                              field,
                              e.target.value,
                            )
                          }
                          onBlur={finishMilestoneEdit}
                          onPressEnter={finishMilestoneEdit}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              finishMilestoneEdit();
                            }
                          }}
                        />
                      );
                    }

                    return (
                      <div
                        role="button"
                        style={{
                          minHeight: 28,
                          display: "flex",
                          alignItems: "center",
                          padding: "2px 4px",
                          cursor: "text",
                          fontSize: 13,
                          fontWeight: 400,
                          lineHeight: 1.25,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                        tabIndex={0}
                        onClick={() =>
                          beginMilestoneEdit(record.uid, field)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            beginMilestoneEdit(record.uid, field);
                          }
                        }}
                      >
                        <Text ellipsis style={{ fontSize: 13, fontWeight: 400, whiteSpace: "nowrap" }}>
                          {value || (
                            <span style={{ color: "#98a2b3", fontStyle: "italic" }}>
                              Add milestone ID
                            </span>
                          )}
                        </Text>
                      </div>
                    );
                  },
                },
                {
                  title: "Milestone Name",
                  dataIndex: "milestoneName",
                  key: "milestoneName",
                  width: "35%",
                  align: "left",
                  render: (value, record) => {
                    const field = "milestoneName";
                    const isEditing =
                      editingCell?.uid === record.uid &&
                      editingCell?.field === field;
                    const index = milestones.findIndex(
                      (item) => item.uid === record.uid,
                    );

                    if (isEditing) {
                      return (
                        <Input.TextArea
                          size="small"
                          value={value || ""}
                          placeholder="Milestone name"
                          autoFocus
                          autoSize={{ minRows: 2, maxRows: 6 }}
                          onChange={(e) =>
                            handleMilestoneFieldChange(
                              index,
                              field,
                              e.target.value,
                            )
                          }
                          onBlur={finishMilestoneEdit}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              finishMilestoneEdit();
                              return;
                            }

                            if (e.key === "Enter") {
                              e.stopPropagation();
                              e.preventDefault();

                              const textarea = e.target;
                              const start = textarea.selectionStart;
                              const end = textarea.selectionEnd;
                              const result = addMilestoneBulletLine(
                                textarea.value,
                                start,
                                end,
                              );

                              handleMilestoneFieldChange(
                                index,
                                field,
                                result.value,
                              );

                              requestAnimationFrame(() => {
                                textarea.selectionStart = result.cursor;
                                textarea.selectionEnd = result.cursor;
                              });
                            }
                          }}
                        />
                      );
                    }

                    return (
                      <div
                        role="button"
                        style={{
                          minHeight: 28,
                          display: "flex",
                          alignItems: "center",
                          padding: "2px 4px",
                          cursor: "text",
                          fontSize: 13,
                          fontWeight: 400,
                          lineHeight: 1.25,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                        tabIndex={0}
                        onClick={() =>
                          beginMilestoneEdit(record.uid, field)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            beginMilestoneEdit(record.uid, field);
                          }
                        }}
                      >
                        {value ? (
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 400,
                              lineHeight: 1.45,
                              whiteSpace: "pre-line",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                              width: "100%",
                            }}
                          >
                            {String(value).split("\n").map((line, lineIndex) => (
                              <div key={`${record.uid}-${lineIndex}`}>
                                {line}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span
                            style={{
                              color: "#98a2b3",
                              fontStyle: "italic",
                            }}
                          >
                            Add milestone name
                          </span>
                        )}
                      </div>
                    );
                  },
                },
                {
                  title: "Start Date",
                  dataIndex: "startDate",
                  key: "startDate",
                  width: "10%",
                  align: "left",
                  render: (value, record) => {
                    const field = "startDate";
                    const isEditing =
                      editingCell?.uid === record.uid &&
                      editingCell?.field === field;
                    const index = milestones.findIndex(
                      (item) => item.uid === record.uid,
                    );
                    if (isEditing) {
                      return (
                        <Input
                          size="small"
                          style={{ width: "100%" }}
                          value={value || ""}
                          placeholder="MMDDYYYY"
                          maxLength={10}
                          autoFocus
                          onChange={(e) => {
                            handleMilestoneFieldChange(
                              index,
                              field,
                              e.target.value,
                            );
                          }}
                          onBlur={finishMilestoneEdit}
                          onPressEnter={finishMilestoneEdit}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              finishMilestoneEdit();
                            }
                          }}
                        />
                      );
                    }

                    return (
                      <div
                        role="button"
                        style={{
                          minHeight: 28,
                          display: "flex",
                          alignItems: "center",
                          padding: "2px 4px",
                          cursor: "text",
                          fontSize: 13,
                          fontWeight: 400,
                          lineHeight: 1.25,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                        tabIndex={0}
                        onClick={() =>
                          beginMilestoneEdit(record.uid, field)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            beginMilestoneEdit(record.uid, field);
                          }
                        }}
                      >
                        <Text type={value ? undefined : "secondary"} style={{ fontSize: 13, fontWeight: 400, whiteSpace: "nowrap" }}>
                          {value || "Select date"}
                        </Text>
                      </div>
                    );
                  },
                },
                {
                  title: "End Date",
                  dataIndex: "endDate",
                  key: "endDate",
                  width: "10%",
                  align: "left",
                  render: (value, record) => {
                    const field = "endDate";
                    const isEditing =
                      editingCell?.uid === record.uid &&
                      editingCell?.field === field;
                    const index = milestones.findIndex(
                      (item) => item.uid === record.uid,
                    );
                    if (isEditing) {
                      return (
                        <Input
                          size="small"
                          style={{ width: "100%" }}
                          value={value || ""}
                          placeholder="MMDDYYYY"
                          maxLength={10}
                          autoFocus
                          onChange={(e) => {
                            handleMilestoneFieldChange(
                              index,
                              field,
                              e.target.value,
                            );
                          }}
                          onBlur={finishMilestoneEdit}
                          onPressEnter={finishMilestoneEdit}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              finishMilestoneEdit();
                            }
                          }}
                        />
                      );
                    }

                    return (
                      <div
                        role="button"
                        style={{
                          minHeight: 28,
                          display: "flex",
                          alignItems: "center",
                          padding: "2px 4px",
                          cursor: "text",
                          fontSize: 13,
                          fontWeight: 400,
                          lineHeight: 1.25,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                        tabIndex={0}
                        onClick={() =>
                          beginMilestoneEdit(record.uid, field)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            beginMilestoneEdit(record.uid, field);
                          }
                        }}
                      >
                        <Text type={value ? undefined : "secondary"} style={{ fontSize: 13, fontWeight: 400, whiteSpace: "nowrap" }}>
                          {value || "Select date"}
                        </Text>
                      </div>
                    );
                  },
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  key: "amount",
                  width: "13%",
                  align: "left",
                  render: (value, record) => {
                    const field = "amount";
                    const isEditing =
                      editingCell?.uid === record.uid &&
                      editingCell?.field === field;
                    const index = milestones.findIndex(
                      (item) => item.uid === record.uid,
                    );

                    // Masked for this role: show as-is, not editable
                    if (isMasked(value)) {
                      return (
                        <Text style={{ fontSize: 13 }}>
                          {value}
                        </Text>
                      );
                    }

                    if (isEditing) {
                      return (
                        <InputNumber
                          size="small"
                          min={0}
                          style={{ width: "100%" }}
                          value={value}
                          prefix={currencySymbol}
                          autoFocus
                          formatter={(val) =>
                            `${val ?? ""}`.replace(
                              /\B(?=(\d{3})+(?!\d))/g,
                              ",",
                            )
                          }
                          parser={(val) =>
                            (val || "").replace(/[^\d.]/g, "")
                          }
                          onChange={(newValue) =>
                            handleMilestoneFieldChange(
                              index,
                              field,
                              newValue === null
                                ? 0
                                : Number(newValue),
                            )
                          }
                          onBlur={finishMilestoneEdit}
                          onPressEnter={finishMilestoneEdit}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              finishMilestoneEdit();
                            }
                          }}
                        />
                      );
                    }

                    const hasAmount =
                      Number.isFinite(Number(value)) &&
                      Number(value) > 0;
                    const formattedAmount = hasAmount
                      ? Number(value).toLocaleString("en-US")
                      : "";

                    return (
                      <div
                        role="button"
                        style={{
                          minHeight: 28,
                          display: "flex",
                          alignItems: "center",
                          padding: "2px 4px",
                          cursor: "text",
                          fontSize: 13,
                          fontWeight: 400,
                          lineHeight: 1.25,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                        tabIndex={0}
                        onClick={() =>
                          beginMilestoneEdit(record.uid, field)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            beginMilestoneEdit(record.uid, field);
                          }
                        }}
                      >
                        <Text type={hasAmount ? undefined : "secondary"}>
                          {hasAmount
                            ? `${currencySymbol}${formattedAmount}`
                            : "Enter amount"}
                        </Text>
                      </div>
                    );
                  },
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  width: "15%",
                  align: "left",
                  render: (value, record) => {
                    const field = "status";
                    const isEditing =
                      editingCell?.uid === record.uid &&
                      editingCell?.field === field;
                    const index = milestones.findIndex(
                      (item) => item.uid === record.uid,
                    );
                    const normalizedStatus =
                      String(value || DEFAULT_MILESTONE_STATUS).trim();

                    if (isEditing) {
                      return (
                        <Select
                          size="small"
                          style={{ width: "100%" }}
                          value={normalizedStatus}
                          options={MILESTONE_STATUS_OPTIONS}
                          open
                          autoFocus
                          onChange={(newValue) => {
                            handleMilestoneFieldChange(
                              index,
                              field,
                              newValue,
                            );
                            finishMilestoneEdit();
                          }}
                          onOpenChange={(open) => {
                            if (!open) finishMilestoneEdit();
                          }}
                        />
                      );
                    }

                    return (
                      <div
                        role="button"
                        style={{
                          minHeight: 28,
                          display: "flex",
                          alignItems: "center",
                          padding: "2px 4px",
                          cursor: "text",
                          fontSize: 13,
                          fontWeight: 400,
                          lineHeight: 1.25,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                        tabIndex={0}
                        onClick={() =>
                          beginMilestoneEdit(record.uid, field)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            beginMilestoneEdit(record.uid, field);
                          }
                        }}
                      >
                        <Text style={{ fontSize: 13, fontWeight: 400, whiteSpace: "nowrap" }}>{normalizedStatus}</Text>
                      </div>
                    );
                  },
                },
                ...(inlineEditable
                  ? [
                    {
                      title: "",
                      key: "actions",
                      width: "5%",
                      align: "center",
                      render: (_value, _record, index) => (
                        <Popconfirm
                          overlayClassName="trackon-root"
                          title="Remove this milestone?"
                          okText="Remove"
                          cancelText="Cancel"
                          onConfirm={() =>
                            handleRemoveMilestone(index)
                          }
                        >
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            aria-label="Remove milestone"
                          />
                        </Popconfirm>
                      ),
                    },
                  ]
                  : []),
              ]}
            />

            <Modal
              className="trackon-root"
              title={
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>Add Milestone</div>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                    Enter the milestone details below. All fields are required.
                  </Text>
                </div>
              }
              open={milestoneModalOpen}
              onCancel={handleMilestoneModalCancel}
              onOk={handleMilestoneModalAdd}
              okText="Add Milestone"
              cancelText="Cancel"
              okButtonProps={{ style: { borderRadius: 4 } }}
              cancelButtonProps={{ style: { borderRadius: 4 } }}
              destroyOnClose
              centered={false}
              width={720}
              styles={{
                body: {
                  paddingTop: 6,
                  maxHeight: "calc(100vh - 180px)",
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingRight: 8,
                },
              }}
              style={{
                borderRadius: 8,
                top: 24,
                maxWidth: "calc(100vw - 32px)",
              }}
              getContainer={() => document.body}
            >

              <Form
                form={milestoneForm}
                layout="vertical"
                requiredMark="optional"
              >
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Milestone ID"
                      name="milestoneId"
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Milestone ID is required.",
                        },
                      ]}
                    >
                      <Input placeholder="e.g. ML10" autoFocus style={{ height: 40 }} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={16}>
                    <Form.Item
                      label="Milestone Name"
                      name="milestoneName"
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Milestone name is required.",
                        },
                      ]}
                    >
                      <Input.TextArea
                        placeholder="Enter milestone name"
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;

                          e.preventDefault();
                          const textarea = e.target;
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const result = addMilestoneBulletLine(
                            textarea.value,
                            start,
                            end,
                          );

                          milestoneForm.setFieldValue(
                            "milestoneName",
                            result.value,
                          );

                          requestAnimationFrame(() => {
                            textarea.selectionStart = result.cursor;
                            textarea.selectionEnd = result.cursor;
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Start Date"
                      name="startDate"
                      rules={[
                        {
                          required: true,
                          message: "Start date is required.",
                        },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();

                            const parsedDate = parseMilestoneDate(value);
                            if (!parsedDate) {
                              return Promise.reject(
                                new Error(
                                  `Enter a valid date.`,
                                ),
                              );
                            }

                            if (
                              milestoneProjectStart?.isValid() &&
                              parsedDate.isBefore(milestoneProjectStart, "day")
                            ) {
                              return Promise.reject(
                                new Error(
                                  "Start date cannot be before the project start date.",
                                ),
                              );
                            }

                            if (
                              milestoneProjectEnd?.isValid() &&
                              parsedDate.isAfter(milestoneProjectEnd, "day")
                            ) {
                              return Promise.reject(
                                new Error(
                                  "Start date cannot be after the project end date.",
                                ),
                              );
                            }

                            const end = parseMilestoneDate(
                              milestoneForm.getFieldValue("endDate"),
                            );

                            if (end && !end.isAfter(parsedDate, "day")) {
                              return Promise.reject(
                                new Error(
                                  "End date must be later than start date.",
                                ),
                              );
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        style={{ width: "100%" }}
                        placeholder="MMDDYYYY"
                        maxLength={10}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      label="End Date"
                      name="endDate"
                      dependencies={["startDate"]}
                      rules={[
                        {
                          required: true,
                          message: "End date is required.",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value) return Promise.resolve();

                            const parsedDate = parseMilestoneDate(value);
                            if (!parsedDate) {
                              return Promise.reject(
                                new Error(
                                  `Enter a valid date.`,
                                ),
                              );
                            }

                            const start = parseMilestoneDate(
                              getFieldValue("startDate"),
                            );

                            if (start && !parsedDate.isAfter(start, "day")) {
                              return Promise.reject(
                                new Error(
                                  "End date must be later than start date.",
                                ),
                              );
                            }

                            if (
                              milestoneProjectStart?.isValid() &&
                              parsedDate.isBefore(milestoneProjectStart, "day")
                            ) {
                              return Promise.reject(
                                new Error(
                                  "End date cannot be before the project start date.",
                                ),
                              );
                            }

                            if (
                              milestoneProjectEnd?.isValid() &&
                              parsedDate.isAfter(milestoneProjectEnd, "day")
                            ) {
                              return Promise.reject(
                                new Error(
                                  "End date cannot be after the project end date.",
                                ),
                              );
                            }

                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <Input
                        style={{ width: "100%" }}
                        placeholder="MMDDYYYY"
                        maxLength={10}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Amount"
                      name="amount"
                      rules={[
                        {
                          required: true,
                          message: "Milestone amount is required.",
                        },
                        {
                          validator: (_, value) => {
                            const numericValue = Number(value);
                            if (
                              Number.isFinite(numericValue) &&
                              numericValue > 0
                            ) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                "Milestone amount must be greater than zero.",
                              ),
                            );
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        placeholder="Enter amount"
                        prefix={currencySymbol}
                        formatter={(val) =>
                          `${val ?? ""}`.replace(
                            /\B(?=(\d{3})+(?!\d))/g,
                            ",",
                          )
                        }
                        parser={(val) =>
                          (val || "").replace(/[^\d.]/g, "")
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Status"
                      name="status"
                      rules={[
                        {
                          required: true,
                          message: "Milestone status is required.",
                        },
                      ]}
                    >
                      <Select
                        options={MILESTONE_STATUS_OPTIONS}
                        placeholder="Select status"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Modal>
          </>

          <Divider />

          {sowData && (
            <div
              style={{
                marginTop: 12,
              }}
            >
              {uploadedFile && (
                <Text
                  style={{
                    display:
                      "block",
                    marginBottom:
                      8,
                    marginTop:
                      18,
                    color:
                      "#7a7676",
                  }}
                >
                  Uploaded File:{" "}
                  <Text>
                    {
                      uploadedFile
                    }
                  </Text>
                </Text>
              )}
            </div>
          )}
        </Card>

        {/* FORM ACTIONS */}
        <Card
          style={{
            borderRadius: 16,
            border:
              "1px solid #f0f0f0",
            background:
              "#fafafa",
          }}
          styles={{
            body: {
              padding:
                "16px 24px",
            },
          }}
        >
          <div
            style={{
              display:
                "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              flexWrap:
                "wrap",
              gap: 12,
            }}
          >
            <span />

            <Space size={12}>
              <Button
                size="large"
                onClick={() => {
                  form.resetFields();

                  setSowUploading(
                    false,
                  );

                  setSowUploadProgress(
                    0,
                  );

                  if (
                    isEdit &&
                    activeProject
                  ) {
                    const editValues =
                      buildEditFormValues(
                        activeProject,
                        accountOptions,
                      );

                    form.setFieldsValue(
                      editValues,
                    );

                    const existingMilestones =
                      readProjectMilestones(
                        activeProject,
                      );

                    setMilestones(
                      existingMilestones,
                    );

                    setSowData(
                      null,
                    );

                    setUploadedFile(
                      "",
                    );
                  } else {
                    setMilestones(
                      [],
                    );

                    setSowData(
                      null,
                    );

                    setUploadedFile(
                      "",
                    );
                  }

                  message.info(
                    "Form reset.",
                  );
                }}
              >
                Reset
              </Button>

              <Button
                type="primary"
                size="large"
                icon={
                  <SaveOutlined />
                }
                loading={saving}
                onClick={
                  handleSave
                }
                style={{
                  minWidth: 120,
                }}
              >
                {isEdit
                  ? "Update"
                  : "Add"}
              </Button>
            </Space>
          </div>
        </Card>
      </Form>
      </DataState>
    </div>
  );
}
